import React, { Fragment, RefObject, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BannerField, IBannerSaveFormProps } from "../../../interfaces/banners";

import { TextArea, TextInput } from "../../reusables/FormElements";
import { SVGIcon } from "../../wrappers/SVGIcon";

import { Icons } from "../../../data/Assets";

import { useCreateBanner, useDeleteBanner, useUpdateBanner, useUploadBannerImage, useDeleteBannerImage } from "../../../hooks/banners";

export const BannerDetailsForm = (props: IBannerSaveFormProps) => {
    const { operation, isLoading, bannerData, formData, setFormData } = props;

    const navigate = useNavigate();

    const requiredFields: BannerField[] = ['title'];

    const imageFileRef = useRef<HTMLInputElement>(null);

    const [imageFile, setImageFile] = useState<File | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isImageUploading, setIsImageUploading] = useState(false);
    const [isImageDeleting, setIsImageDeleting] = useState(false);

    const { mutate: createBanner } = useCreateBanner();
    const { mutate: updateBanner } = useUpdateBanner();
    const { mutate: deleteBanner } = useDeleteBanner();
    const { mutate: deleteImage } = useDeleteBannerImage();
    const { mutate: uploadImage } = useUploadBannerImage();

    useEffect(() => {
        if (bannerData) {
            setFormData({
                id: bannerData.id,
                title: { value: bannerData.title, error: '' },
                description: { value: bannerData.description, error: '' },
            });
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bannerData]);

    const onChangeText = (name: BannerField, value: string) => {
        setFormData({
            ...formData,
            [name]: { value, error: '' },
        });
    };

    const getProcessing = () => {
        return isSaving || isImageUploading || isImageDeleting || isDeleting;
    };

    const isSaveDisabled = () => {
        const emptyFields = requiredFields.some(field => {
            let value = formData[field].value;
            return !value?.trim()?.length;
        });

        const isDataSame =
            formData.title.value === (bannerData?.title ?? '') &&
            formData.description.value === (bannerData?.description ?? '')

        return emptyFields || isDataSame;
    };

    const onOpenFile = (fileRef: RefObject<HTMLInputElement>) => {
        if (getProcessing()) return;

        if (fileRef.current) {
            fileRef.current.click();
        };
    };

    const onFileChange = (fileRef: RefObject<HTMLInputElement>) => {
        if (getProcessing()) return;

        if (fileRef.current?.files) {
            const file = fileRef.current.files[0];

            setImageFile(file);

            fileRef.current.value = '';

            if (formData.id) {
                uploadImage({
                    id: formData.id,
                    file,
                    setIsImageUploading,
                    setImageFile,
                });
            };
        };
    };

    const onSaveData = () => {
        if (isSaveDisabled() || getProcessing()) return;

        setFormData(prevData => {
            const newData = { ...prevData };
            Object.keys(newData).forEach(key => {
                if (key === "id") return;
                newData[key as BannerField].error = "";
            });

            return newData;
        });

        if (formData.id) {
            updateBanner({
                id: formData.id,
                payload: {
                    title: formData.title.value,
                    description: formData.description.value,
                },
                setIsSaving,
                setImageFile,
            });
        } else {
            createBanner({
                payload: {
                    title: formData.title.value,
                    description: formData.description.value,
                    file: imageFile,
                },
                setIsSaving,
                setImageFile,
            });
        };
    };

    const onDeleteImage = () => {
        if (getProcessing()) return;

        if (!formData.id) {
            setImageFile(null);
        } else {
            deleteImage({
                id: formData.id,
                setIsImageDeleting,
                setImageFile,
            });
        };
    };

    const onDeleteBanner = () => {
        if (getProcessing()) return;

        if (!formData.id) {
            setImageFile(null);
        } else {
            deleteBanner({
                id: formData.id,
                setIsDeleting,
            });
        };
    };

    return (
        <div className="immersion__details_form">
            <div className="immersion__details_form_inputs__container">
                <div className="immersion__banner_details__upload_actions">
                    <input className="immersion__file_upload_sudo_input" ref={imageFileRef} type="file" accept="image/*" onChange={() => onFileChange(imageFileRef)} />
                    {bannerData?.image_url
                        ? <div className="immersion__file_upload_preview">
                            <img src={bannerData.image_url} alt={`Banner: ${bannerData.title}`} />
                            <button
                                className="immersion__file_upload_preview__delete_btn"
                                onClick={onDeleteImage}
                            >
                                {isImageDeleting
                                    ? <i className='immersion__btn_loader' />
                                    : <SVGIcon src={Icons.Trash3Fill} />
                                }
                            </button>
                        </div>
                        : <Fragment>
                            {imageFile && !isImageUploading
                                ? <div className="immersion__file_upload_preview">
                                    <img src={URL.createObjectURL(imageFile)} alt={`Uploaded banner preview`} />
                                    <button
                                        className="immersion__file_upload_preview__delete_btn"
                                        onClick={onDeleteImage}
                                    >
                                        {isImageDeleting
                                            ? <i className='immersion__btn_loader' />
                                            : <SVGIcon src={Icons.Trash3Fill} />
                                        }
                                    </button>
                                </div>
                                : <button
                                    className="immersion__file_upload_preview immersion__file_upload_preview__upload_btn"
                                    onClick={() => onOpenFile(imageFileRef)}
                                >
                                    {isImageUploading
                                        ? <i className='immersion__btn_loader' />
                                        : <SVGIcon src={Icons.Upload2} />
                                    }
                                    <p className="immersion__file_upload_preview__upload_btn__text">{isImageUploading ? 'Uploading...' : 'Upload Image'}</p>
                                    <p className="immersion__file_upload_preview__upload_btn__recomm_text">240x240px recommended<br />1:1 aspect ratio</p>
                                </button>
                            }
                        </Fragment>
                    }
                </div>
                <div className="immersion__details_form_inputs">
                    <TextInput
                        name="title"
                        label="Title"
                        type="text"
                        value={formData.title.value}
                        onChange={(value: string) => onChangeText('title', value)}
                        errorMsg={formData.title.error}
                        placeholder="Enter title"
                        read_only={isLoading}
                        max_length={100}
                        required={true}
                    />
                    <TextArea
                        name="description"
                        label="Description"
                        value={formData.description.value}
                        onChange={(value: string) => onChangeText('description', value)}
                        errorMsg={formData.description.error}
                        placeholder="Enter description"
                        read_only={isLoading}
                        max_length={500}
                        length_flex={2}
                    />
                </div>
            </div>
            <div className="immersion__details_form_actions">
                <button
                    className={`immersion__btn immersion__btn__secondary ${getProcessing() ? "immersion__btn__secondary__disabled" : ""}`}
                    onClick={() => getProcessing() ? null : navigate('/banners')}
                >
                    {operation === 'create' ? "Discard" : "Go back"}
                </button>
                {operation === 'update' && (
                    <button
                        className={`immersion__btn immersion__btn__primary ${getProcessing() ? "immersion__btn__primary__disabled" : ""}`}
                        onClick={onDeleteBanner}
                    >
                        {isDeleting ? <i className='immersion__btn_loader' /> : 'Delete'}
                    </button>
                )}
                <button
                    className={`immersion__btn immersion__btn__primary ${isSaveDisabled() ? "immersion__btn__primary__disabled" : ""}`}
                    onClick={onSaveData}
                >
                    {isSaving ? <i className='immersion__btn_loader' /> : 'Save'}
                </button>
            </div>
        </div>
    );
};